import _ from 'lodash';
import Debug from '@/util/debug';
import 'vue-awesome/icons/folder-open';
import debounce from 'debounce';
import Loading from '@/components/loading/Loading.vue';
import { PROPOSALS_COLUMNS } from '@/util/constants';

const debug = Debug('proposals-remote');

export default {
  dependencies: [
    'genericConfigService',
    'draftService',
    'adApiService',
  ],
  components: {
    loading: Loading,
  },
  data() {
    return {
      loading: true,
      error: null,
      loadSize: 10,
      totalCount: 0,
      canLoadMore: true,
      fetchingDrafts: false,
      tabIndex: 0,
      feature_upsells: false,
      tabs: {
        drafts: { name: 'pos.proposals.tabs.drafts', filter: { hasCampaign: false, inCheckout: false } },
        checkout: { name: 'pos.proposals.tabs.checkout', filter: { hasCampaign: false, inCheckout: true } },
        completed: { name: 'pos.proposals.tabs.completed', filter: { hasCampaign: true } },
        live: { name: 'pos.proposals.tabs.live', filter: { hasCampaign: true, campaignOnline: true } },
      },
      filter_string: null, // debounced from filter_string_ui
      filter_string_ui: null, // used for UI
      count: 0,
      column_configuration: null,
      agent_proposal_checkout_tab: null,
    };
  },
  methods: {
    openDraft(item) {
      this.$router.push({
        name: item.currentStep === 'campaign-checkout' && item.campaign_id ? 'proposal' : 'campaign-wrapper',
        params: {
          id: item.id,
        },
      });
    },
    loadMore() {
      this.loadSize += 10;
    },
    provideItems(ctx) {
      const tab = this.tabs[Object.keys(this.tabs)[this.tabIndex]];
      const params = {
        limit: ctx.perPage,
        sort: {
          created_at: 'desc',
        },
        filter: {
          ...tab.filter,
        },
      };
      if (ctx.filter) {
        params.filter.text = ctx.filter;
      }
      return Promise.resolve()
        .then(() => this.fetchingDrafts = true)
        .then(() => this.adApiService.rawRequest('GET', 'drafts/search', params))
        .then(res => {
          this.canLoadMore = _.get(res, 'count.filtered', 0) > this.loadSize;
          this.totalCount = _.get(res, 'count.filtered', 0);
          this.fetchingDrafts = false;
          return this.draftService.fixSearchDrafts(res.items);
        })
        .catch(err => {
          debug.error(err);
          this.error = String(err);
          this.fetchingDrafts = false;
          return [];
        });
    },
    setActiveTabFromRoute() {
      const routeTab = this.$route.params.tab ? this.$route.params.tab : Object.keys(this.tabs)[0];
      this.tabIndex = Object.keys(this.tabs).indexOf(routeTab);
      if (this.tabIndex < 0 || this.tabIndex >= this.tabs.length) {
        this.tabIndex = 0;
      }
    },
    onTabChanged(value) {
      this.loadSize = 10;
      this.totalCount = 0;
      this.canLoadMore = true;
      const tabKey = Object.keys(this.tabs)[value];
      this.$router.replace({ name: 'proposals', params: { tab: tabKey } });
    },
    isSuperUser() {
      if (this.$store.user && this.$store.user.groups) {
        for (let i = this.$store.user.groups.length - 1; i > -1; i -= 1) {
          if (this.$store.user.groups[i].name === 'Super User') {
            return true;
          }
        }
      }
      return false;
    },
    getVisibleColumns(tab) {
      const visibleCols = _.get(this.column_configuration, tab, null);
      if (Array.isArray(visibleCols) && visibleCols.length > 0) {
        return visibleCols;
      } else if (typeof PROPOSALS_COLUMNS[tab] !== 'undefined') {
        return PROPOSALS_COLUMNS[tab];
      }
      return [];
    },
    get: _.get,
  },
  computed: {
    columnConfiguration() {
      const r = {
        created: { label: this.$t('draft_field.title.created'), sortable: false, class: 'created' },
        code: { label: this.$t('draft_field.title.code'), sortable: false, class: 'code' },
        company: { label: this.$t('draft_field.title.company'), sortable: false, class: 'company' },
        customer_remote_id: { label: this.$t('draft_field.title.customer-remote-id'), sortable: false, class: 'customer_remote_id' },
        locations: { label: this.$t('draft_field.title.locations'), sortable: false, class: 'locations' },
        name: { label: this.$t('draft_field.title.name'), sortable: false, class: 'name' },
        nextStep: { label: this.$t('draft_field.title.step'), sortable: false, class: 'step' },
        status: { label: this.$t('draft_field.title.status'), sortable: false, class: 'status' },
        target: { label: this.$t('draft_field.title.target'), sortable: false, class: 'target' },
        goal: { label: this.$t('draft_field.title.campaign-goal'), sortable: false, class: 'goal' },
      };
      if (this.isSuperUser) {
        r.creator = { label: this.$t('draft_field.title.creator'), sortable: false, class: 'creator' };
      }
      return r;
    },
    options: function computedOptions() {
      const fields = (tabName) => {
        const r = {};
        this.getVisibleColumns(tabName).forEach(colname => {
          if (typeof this.columnConfiguration[colname] !== 'undefined') {
            r[colname] = this.columnConfiguration[colname];
          }
        });
        return r;
      };

      return {
        drafts: { fields: fields('drafts'), filter: null },
        checkout: { fields: fields('checkout'), filter: null },
        completed: { fields: fields('completed'), filter: null },
        live: { fields: fields('live'), filter: null },
      };
    },
  },
  watch: {
    filter_string_ui: debounce(function watchFilterString(after) {
      debug.info('Setting filter_string to %s', after);
      this.filter_string = after;
    }, 500),
    $route() {
      this.setActiveTabFromRoute();
    },
  },
  mounted() {
    this.genericConfigService.getConfig()
      .then(c => {
        this.feature_upsells = c.feature_upsells;
        this.column_configuration = _.get(c, 'column_configuration.agent', null);
        this.agent_proposal_checkout_tab = c.agent_proposal_checkout_tab;
      })
      .then(() => this.setActiveTabFromRoute())
      .then(() => {
        if (!this.feature_upsells) {
          this.$delete(this.tabs, 'live');
        }
        if (this.agent_proposal_checkout_tab) {
          this.$delete(this.tabs, 'checkout');
          this.$delete(this.tabs.drafts.filter, 'inCheckout');
        }
      })
      .then(() => this.loading = false)
      .catch(err => {
        this.error = String(err);
        this.loading = false;
      });
  },
};



// WEBPACK FOOTER //
// ./src/components/proposals-remote/ProposalsRemote.js